import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import DateDayjstUtils from '@date-io/dayjs';
import * as Yup from 'yup';
import { postReplanActivity } from 'services/activity';
import ptBR from 'dayjs/locale/pt-br';
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { IModalReplanningProps } from './types';
import useStyles from './style';

function ModalReplanning({
  children,
  onClose,
  open,
  selectedRows,
  reloadData,
}: IModalReplanningProps) {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const validationSchema = Yup.object({
    replanningStartDate: Yup.string().required(),
    replanningFinishDate: Yup.string().required(),
  });

  const formik = useFormik({
    initialValues: {
      replanningStartDate: null,
      replanningFinishDate: null,
    },
    onSubmit: () => {
      const { replanningStartDate, replanningFinishDate } = formik.values;

      const params = {
        id: selectedRows,
        start: dayjs(replanningStartDate).format('YYYY-MM-DDTHH:mmZ'),
        completion: dayjs(replanningFinishDate).format('YYYY-MM-DDTHH:mmZ'),
      };

      setLoadingSaveForm(true);
      // se for enviado com sucesso
      postReplanActivity(params)
        .then(() => {
          // solicita uma recarga de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
    validationSchema,
  });

  const [activeButton, setActiveButton] = useState(false);

  useEffect(() => {
    if (
      dayjs(formik.values.replanningStartDate).isValid() &&
      dayjs(formik.values.replanningFinishDate).isValid() &&
      dayjs(formik.values.replanningFinishDate) >=
        dayjs(formik.values.replanningStartDate)
    ) {
      setActiveButton(true);
    } else {
      setActiveButton(false);
    }
  }, [formik.values.replanningStartDate, formik.values.replanningFinishDate]);

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  React.useEffect(() => {
    // se existir alteracoes no campo & estiver com a modal fechada
    if (formik.dirty && !open) {
      // então: reset form quando eu fecho o modal,
      formik.resetForm();
    }
  }, [formik, open]);

  const extraPropsStartDate: { helperText?: string } = {};
  const extraPropsFinishDate: { helperText?: string } = {};

  if (!formik.values.replanningStartDate) {
    extraPropsStartDate.helperText = 'Obrigatório.';
  }
  if (!formik.values.replanningFinishDate) {
    extraPropsFinishDate.helperText = 'Obrigatório.';
  }

  const body = (
    <div className={classes.modal}>
      <div className={classes.modalHeader}>
        <Typography variant="subtitle1" className={classes.headerTitle}>
          Replanejar
        </Typography>
        <Close className={classes.headerIcon} onClick={onClose} />
      </div>
      <div className={classes.modalText}>
        <Typography variant="body2">
          Preencha os dados obrigatórios para replanejar a atividade:
        </Typography>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.modalBody}>
          <MuiPickersUtilsProvider locale={ptBR} utils={DateDayjstUtils}>
            <div className={classes.textfield}>
              <KeyboardDateTimePicker
                className={classes.root}
                name="replanningStartDate"
                clearable
                ampm={false}
                onChange={date => handleDateChange('replanningStartDate', date)}
                value={formik.values.replanningStartDate}
                placeholder="DD/MM/YY - HH:mm"
                inputVariant="outlined"
                label="Data de início"
                // validacao temporariamente comentada a pedido da cliente
                // minDate={new Date()}
                minDateMessage="Preenchimento inválido."
                format="DD/MM/YY - HH:mm"
                invalidDateMessage="Preenchimento inválido."
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                {...extraPropsStartDate}
              />
            </div>
            <div className={classes.textfield}>
              <KeyboardDateTimePicker
                className={classes.root}
                name="replanningFinishDate"
                clearable
                ampm={false}
                onChange={date => {
                  return handleDateChange('replanningFinishDate', date);
                }}
                value={formik.values.replanningFinishDate}
                placeholder="DD/MM/YY - HH:mm"
                inputVariant="outlined"
                label="Data de conclusão"
                minDate={
                  formik.values.replanningStartDate !== null
                    ? formik.values.replanningStartDate
                    : new Date()
                }
                minDateMessage="Preenchimento inválido."
                format="DD/MM/YY - HH:mm"
                invalidDateMessage="Preenchimento inválido."
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                {...extraPropsFinishDate}
              />
            </div>
          </MuiPickersUtilsProvider>
        </div>
        <div className={classes.modalFooter}>
          <Button onClick={() => onClose()} color="primary">
            Cancelar
          </Button>
          <ButtonWithLoading
            color="primary"
            id="btn"
            type="submit"
            disabled={!activeButton}
            isLoading={loadingSaveForm}
          >
            Salvar
          </ButtonWithLoading>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {children}
      <div className={classes.container}>
        <Modal open={open} onClose={onClose}>
          {body}
        </Modal>
      </div>
    </>
  );
}

export default ModalReplanning;
